/**
 * Arranging a turn's segments for rendering: the work folds into one strip, the
 * answer stands on its own.
 *
 * Split out of `../messageTimeline.ts`. This is a separate concern from building
 * the timeline: `buildMessageTimeline` decides WHAT happened and in what order,
 * this decides how much VISUAL WEIGHT each part of it gets. The strip preserves
 * stream order inside itself, so nothing here can reintroduce the
 * render-by-type inversion the timeline exists to prevent.
 */

import type { ActivityBlock, TimelineSegment, TurnLayoutBlock } from './types.js';

/**
 * Segment kinds that must keep their own position and full weight:
 * an approval needs a decision, an artifact is a deliverable, a plan is the
 * roadmap for the work, and context is provenance. Collapsing any of them would
 * hide something the user has to see or act on.
 */
const STANDALONE_KINDS = new Set<TimelineSegment['kind']>([
  'approval',
  'artifact',
  'plan',
  'context',
]);

/**
 * The final answer is the TRAILING run of text segments — text with nothing but
 * text after it.
 *
 * Not "the last text segment": an answer that streams as two adjacent text
 * parts would then have its first half swallowed by the activity strip. And not
 * "the last text anywhere": mid-turn narration would sit outside the strip
 * while work was still streaming and then jump into it when the answer arrived,
 * which is exactly the content-jump the timeline was written to kill.
 */
function trailingTextRunStart(segments: readonly TimelineSegment[]): number {
  let index = segments.length;
  while (index > 0 && segments[index - 1]!.kind === 'text') index -= 1;
  return index;
}

export function buildTurnLayout(segments: readonly TimelineSegment[]): TurnLayoutBlock[] {
  const answerStart = trailingTextRunStart(segments);
  const blocks: TurnLayoutBlock[] = [];
  let activity: ActivityBlock | null = null;
  let activityCounter = 0;

  segments.forEach((segment, index) => {
    const isAnswer = index >= answerStart;
    if (isAnswer || STANDALONE_KINDS.has(segment.kind)) {
      activity = null;
      blocks.push({ kind: 'segment', key: segment.key, segment });
      return;
    }
    if (!activity) {
      activity = { kind: 'activity', key: `activity-${activityCounter++}`, segments: [] };
      blocks.push(activity);
    }
    activity.segments.push(segment);
  });

  /**
   * A strip folds WORK. A block that turned out to be nothing but prose has no
   * work to fold, so it renders as prose — otherwise the sentence that sets up
   * an approval ("Drafting the email…") would be hidden behind a chevron
   * directly above the button the user has to press.
   */
  return blocks.flatMap<TurnLayoutBlock>((block) => {
    if (block.kind !== 'activity') return [block];
    if (block.segments.some((segment) => segment.kind !== 'text')) return [block];
    return block.segments.map((segment) => ({
      kind: 'segment' as const,
      key: segment.key,
      segment,
    }));
  });
}
